import { motion } from "framer-motion";
import { MapPin, Briefcase } from "lucide-react";
import StaggerWords from "@/components/site/StaggerWords";

const PORTRAIT = "https://customer-assets.emergentagent.com/job_project-portfolio-39/artifacts/eigzcik2_IMG_8376.PNG";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.7, ease: [0.22, 1, 0.36, 1] } }),
};

export default function About() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="bg-ink relative py-24 md:py-40 px-6 md:px-12"
    >
      {/* Connecting rail */}
      <div aria-hidden className="absolute left-3 md:left-6 top-0 bottom-0 w-px bg-[#E04F2A]/25 z-0" />
      <div aria-hidden className="absolute left-[10px] md:left-[19px] top-24 md:top-40 -translate-x-1/2 h-2.5 w-2.5 rounded-full border border-[#E04F2A] bg-ink z-0" />
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-100px" }}
        className="max-w-7xl mx-auto relative z-10"
      >
        <motion.div variants={fadeUp} custom={0} className="flex items-baseline gap-4 mb-12">
          <span className="font-manrope text-xs uppercase tracking-[0.3em] text-bone/60 inline-flex items-center gap-2">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#E04F2A]" />
            02 · About
          </span>
          <span className="h-px flex-1 bg-bone/15" />
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Portrait — sticky on desktop */}
          <motion.div variants={fadeUp} custom={1} className="md:col-span-4 md:sticky md:top-32 md:self-start">
            <div className="aspect-[3/4] overflow-hidden bg-ink-surface relative">
              <motion.img
                src={PORTRAIT}
                alt="Samuel Ortiz"
                className="h-full w-full object-cover"
                initial={{ scale: 1.08 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
              />
              <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between font-manrope text-[10px] uppercase tracking-[0.25em] text-bone/80">
                <span>Samuel Ortiz</span>
                <span>2025</span>
              </div>
            </div>
          </motion.div>

          {/* Bio */}
          <motion.div variants={fadeUp} custom={2} className="md:col-span-8 flex flex-col gap-8">
            <h2 className="font-oswald uppercase tracking-tight leading-[0.95] text-bone text-5xl md:text-6xl lg:text-7xl">
              <StaggerWords text="Design with" />
              <br />
              <StaggerWords
                text="purpose,"
                italic
                italicClassName="font-cormorant italic font-normal tracking-normal"
                delay={0.15}
              />
              <br />
              <StaggerWords text="direction with vision." delay={0.35} />
            </h2>

            <p className="font-manrope font-light text-base md:text-lg text-bone/70 max-w-2xl leading-relaxed">
              Art Director with <span className="text-bone">10 years of experience</span> at
              leading advertising agencies. Expert in creating visual strategies for the{" "}
              <span className="text-bone">consumer</span>,{" "}
              <span className="text-bone">banking</span>,{" "}
              <span className="text-bone">telecommunications</span> and{" "}
              <span className="text-bone">pharma</span> industries. Specialist in social media
              content production, 2D animation and the optimization of digital processes through
              the use of cutting-edge tools and <span className="text-bone">A.I.</span>
            </p>

            {/* Bento info cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 max-w-xl">
              <InfoCard label="Location" value="Mexico City, MX" icon={<MapPin className="h-4 w-4" />} />
              <InfoCard label="Role" value="Art Director" icon={<Briefcase className="h-4 w-4" />} />
            </div>

            {/* Skills */}
            <div className="mt-6">
              <div className="font-manrope text-[10px] uppercase tracking-[0.3em] text-bone/50 mb-4">
                Tools
              </div>
              <div className="flex flex-wrap gap-2">
                {["Photoshop", "After Effects", "Illustrator", "InDesign", "Premiere", "Adobe Firefly", "Midjourney", "Runway", "ChatGPT"].map(
                  (s) => (
                    <span
                      key={s}
                      data-testid={`skill-${s.toLowerCase().replace(/\s+/g, "-")}`}
                      className="font-manrope text-sm border border-ink-border px-4 py-2 text-bone/90 hover:bg-bone hover:text-ink transition-colors"
                    >
                      {s}
                    </span>
                  )
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
}

function InfoCard({ label, value, icon, accent }) {
  return (
    <div
      className={`border border-ink-border p-4 ${
        accent ? "bg-bone text-ink" : "bg-ink-surface text-bone"
      }`}
    >
      <div className="flex items-center gap-2 mb-3">
        {icon}
        <span className={`font-manrope text-[10px] uppercase tracking-[0.25em] ${accent ? "text-ink/70" : "text-bone/50"}`}>
          {label}
        </span>
      </div>
      <div className="font-oswald uppercase tracking-tight text-lg">
        {value}
      </div>
    </div>
  );
}
